import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Pressable, View, StyleSheet, SafeAreaView } from "react-native";
import { LayoutGrid, Activity, ShieldCheck, FileText, AlertTriangle, X } from "lucide-react";
import { useDebounce } from "use-debounce";
import { CatalogTab } from "../../components/adminDashboard/Integrations/CatalogTab/CatalogTab";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import AdminTabNavigator from "../../components/adminDashboard/AdminTabNavigator/AdminTabNavigator";
import { ConnectorDetails } from "../../components/adminDashboard/Integrations/ConnectorDetails/ConnectorDetails";
import ActiveConnectionTab, {
  UserConnectionAggregate,
} from "../../components/adminDashboard/Integrations/ActiveConnectionTab/ActiveConnectionTab";
import {
  ConnectorAuthMode,
  ConnectorType,
  GetAuditEventsDocument,
  GetConnectorHealthQuery,
  useGetAuditEventsQuery,
  useDisableConnectorMutation,
  useEnableConnectorMutation,
  useGetConnectorCatalogQuery,
  useGetConnectorHealthQuery,
  useGetEnabledConnectorsQuery,
  useSetConnectorRoleAccessMutation,
  useTestConnectorConnectionMutation,
} from "@axio/graphql/generated/graphql";
import { UserRoleUI } from "../../constants/rolesAndPermissions";
import {
  buildConnectorStateMaps,
  CatalogConnector,
  ConnectionStatus,
  ConnectorActionResult,
  ConnectorCredentialConfig,
  EnabledConnectorInstance,
  type InstitutionalConnectionSummary,
} from "../../components/adminDashboard/Integrations/catalog.utils";
import { AuditLogTab } from "../../components/adminDashboard/Integrations/AuditLogTab/AuditLogTab";
import { AccessControlTab } from "../../components/adminDashboard/Integrations/AccessControlTab/AccessControlTab";
import { useConnectorStatusToasts } from "../../hooks/integrations/useConnectorStatusToasts";
import { getEnabledRolesForMinimumRole } from "../../utils/integrations/roleAccess";

const TABS: { key: string; title: string; icon: React.ReactNode }[] = [
  { key: "catalog", title: "Catalog", icon: <LayoutGrid size={16} color={"#2563EB"} /> },
  { key: "active", title: "Active Connections", icon: <Activity size={16} color={"#2563EB"} /> },
  { key: "rbac", title: "Access Control", icon: <ShieldCheck size={16} color={"#2563EB"} /> },
  { key: "audit", title: "Audit Log", icon: <FileText size={16} color={"#2563EB"} /> },
];
const getInstitutionalIssueBannerText = (issueCount: number) =>
  issueCount === 1
    ? "1 institutional connection needs attention"
    : `${issueCount} institutional connections need attention`;

const isCatalogConnector = (connector: CatalogConnector | null): connector is CatalogConnector => connector !== null;
const isEnabledConnectorInstance = (
  connectorInstance: EnabledConnectorInstance | null
): connectorInstance is EnabledConnectorInstance => connectorInstance !== null;
const isConnectorHealthStatus = (
  healthStatus: NonNullable<GetConnectorHealthQuery["getConnectorHealth"]["data"]>[number] | null
): healthStatus is NonNullable<NonNullable<GetConnectorHealthQuery["getConnectorHealth"]["data"]>[number]> =>
  healthStatus !== null;
const isUnhealthyInstitutionalConnection = (connection: InstitutionalConnectionSummary): boolean =>
  connection.status === "failed" || connection.status === "degraded";

const DEFAULT_ENABLED_ROLES: string[] = getEnabledRolesForMinimumRole(UserRoleUI.STUDENT);

export default function IntegrationsView() {
  const [activeTab, setActiveTab] = useState<string>("catalog");
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [auditEventSearchQuery, setAuditEventSearchQuery] = useState("");
  const [auditEventConnectorFilter, setAuditEventConnectorFilter] = useState("all");
  const [savingRoleAccessConnectorType, setSavingRoleAccessConnectorType] = useState<ConnectorType | null>(null);
  const [roleAccessErrorMessage, setRoleAccessErrorMessage] = useState<string | null>(null);
  const [dismissedInstitutionalIssueSignature, setDismissedInstitutionalIssueSignature] = useState<string | null>(null);
  const [debouncedAuditEventSearchQuery] = useDebounce(auditEventSearchQuery, 300);
  const auditEventQueryVariables = useMemo(
    () => ({
      filter: {
        subjectTypes: ["connector", "ingestion"],
        ...(auditEventConnectorFilter !== "all" && { connectorType: auditEventConnectorFilter }),
        ...(debouncedAuditEventSearchQuery.trim() && { search: debouncedAuditEventSearchQuery.trim() }),
      },
    }),
    [auditEventConnectorFilter, debouncedAuditEventSearchQuery]
  );

  const {
    data,
    loading,
    error,
    refetch: refetchConnectorCatalog,
  } = useGetConnectorCatalogQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });
  const { data: enabledConnectorsData, refetch: refetchEnabledConnectors } = useGetEnabledConnectorsQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    pollInterval: 30000,
  });
  const { data: connectorHealthData, refetch: refetchConnectorHealth } = useGetConnectorHealthQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    pollInterval: activeTab === "active" ? 30000 : undefined,
  });
  const { data: auditEventsData, refetch: refetchAuditEvents } = useGetAuditEventsQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    variables: auditEventQueryVariables,
  });
  const [enableConnectorMutation] = useEnableConnectorMutation();
  const [disableConnectorMutation] = useDisableConnectorMutation();
  const [setConnectorRoleAccessMutation] = useSetConnectorRoleAccessMutation();
  const [testConnectorConnectionMutation] = useTestConnectorConnectionMutation();

  const connectorCatalog = useMemo(() => {
    const result = data?.getConnectorCatalog;

    if (!result || result.success === false || !result.data) {
      return [];
    }

    return result.data.filter(isCatalogConnector);
  }, [data]);
  const enabledConnectors = useMemo(() => {
    const result = enabledConnectorsData?.getEnabledConnectors;

    if (!result || result.success === false || !result.data) {
      return [];
    }

    return result.data.filter(isEnabledConnectorInstance);
  }, [enabledConnectorsData]);
  const connectorHealthStatuses = useMemo(() => {
    const result = connectorHealthData?.getConnectorHealth;

    if (!result || result.success === false || !result.data) {
      return [];
    }

    return result.data.filter(isConnectorHealthStatus);
  }, [connectorHealthData]);
  const auditEvents = useMemo(() => {
    const result = auditEventsData?.getAuditEvents;

    if (!result || result.success === false || !result.data) {
      return [];
    }

    return result.data;
  }, [auditEventsData]);
  const { approvalMap, connectionMap } = useMemo(
    () => buildConnectorStateMaps(connectorCatalog, enabledConnectors),
    [connectorCatalog, enabledConnectors]
  );
  const connectorHealthByType = useMemo(
    () =>
      new Map(
        connectorHealthStatuses.map((healthStatus) => [healthStatus.connectorInstance.connectorType, healthStatus])
      ),
    [connectorHealthStatuses]
  );

  const catalogErrorMessage =
    error?.message ??
    (data?.getConnectorCatalog.success === false
      ? (data.getConnectorCatalog.message ?? "We were not able to load integrations.")
      : null);

  const selectedConnector = useMemo(
    () => connectorCatalog.find((connector) => connector.id === selectedConnectorId) ?? null,
    [connectorCatalog, selectedConnectorId]
  );
  const selectedApproval = useMemo(
    () => (selectedConnector ? (approvalMap.get(selectedConnector.id) ?? null) : null),
    [approvalMap, selectedConnector]
  );
  const selectedConnection = useMemo(
    () => (selectedConnector ? (connectionMap.get(selectedConnector.id) ?? null) : null),
    [connectionMap, selectedConnector]
  );

  const userConnectionAggregates = useMemo<UserConnectionAggregate[]>(
    () =>
      connectorCatalog
        .map((connector) => {
          const healthStatus = connectorHealthByType.get(connector.connectorType);

          if (!healthStatus?.userConnectionCount) {
            return null;
          }

          const totalConnections = healthStatus.userConnectionCount;
          const expiringConnections = healthStatus.expiringConnectionCount ?? 0;
          const expiredConnections = healthStatus.expiredConnectionCount ?? 0;
          const failedConnections =
            expiredConnections +
            (healthStatus.revokedConnectionCount ?? 0) +
            (healthStatus.unknownConnectionCount ?? 0);
          const activeConnections =
            healthStatus.healthyConnectionCount != null
              ? healthStatus.healthyConnectionCount + expiringConnections
              : Math.max(0, totalConnections - failedConnections);
          const status: ConnectionStatus =
            activeConnections > 0 ? "working" : expiredConnections > 0 ? "expired" : "failed";

          return {
            connectorId: connector.id,
            totalConnections,
            activeConnections,
            failedConnections,
            status,
            byRole: {
              faculty: 0,
              student: totalConnections,
              admin: 0,
            },
          };
        })
        .filter((aggregate): aggregate is UserConnectionAggregate => aggregate !== null),
    [connectorCatalog, connectorHealthByType]
  );
  const institutionalConnectionIssues = useMemo(
    () => Array.from(connectionMap.values()).filter(isUnhealthyInstitutionalConnection),
    [connectionMap]
  );
  const institutionalIssueSignature = useMemo(
    () =>
      institutionalConnectionIssues
        .map((connection) => `${connection.id}:${connection.status}:${connection.errorMessage ?? ""}`)
        .sort()
        .join("|"),
    [institutionalConnectionIssues]
  );
  const institutionalIssueCount = institutionalConnectionIssues.length;
  const showInstitutionalIssueBanner =
    institutionalIssueCount > 0 && dismissedInstitutionalIssueSignature !== institutionalIssueSignature;
  const institutionalIssueBannerText = getInstitutionalIssueBannerText(institutionalIssueCount);

  useEffect(() => {
    if (institutionalIssueCount === 0 && dismissedInstitutionalIssueSignature) {
      setDismissedInstitutionalIssueSignature(null);
    }
  }, [dismissedInstitutionalIssueSignature, institutionalIssueCount]);

  const handleSelectConnector = (connectorId: string) => {
    setSelectedConnectorId(connectorId);
    setDetailPanelOpen(true);
  };

  const runEnableConnector = async (
    connector: CatalogConnector,
    authMode: ConnectorAuthMode,
    config: ConnectorCredentialConfig
  ): Promise<ConnectorActionResult> => {
    try {
      const response = await enableConnectorMutation({
        variables: {
          input: {
            connectorType: connector.connectorType,
            authMode,
            enabledRoles: DEFAULT_ENABLED_ROLES,
            config,
          },
        },
      });
      const result = response.data?.enableConnector;

      if (!result) {
        return {
          success: false,
          message: "We were not able to enable this connector.",
        };
      }

      if (result.success) {
        await Promise.all([refetchConnectorCatalog(), refetchEnabledConnectors(), refetchAuditEvents()]);
      } else {
        await refetchAuditEvents();
      }

      return {
        success: result.success,
        message: result.message,
      };
    } catch (mutationError) {
      await refetchAuditEvents();
      return {
        success: false,
        message: mutationError instanceof Error ? mutationError.message : "We were not able to enable this connector.",
      };
    }
  };

  const runDisableConnector = async (
    connector: CatalogConnector,
    disconnectUserConnections: boolean
  ): Promise<ConnectorActionResult> => {
    try {
      const response = await disableConnectorMutation({
        variables: {
          input: {
            connectorType: connector.connectorType,
            disconnectUserConnections,
          },
        },
      });
      const result = response.data?.disableConnector;

      if (!result) {
        return {
          success: false,
          message: "We were not able to disable this connector.",
        };
      }

      if (result.success) {
        await Promise.all([refetchConnectorCatalog(), refetchEnabledConnectors(), refetchAuditEvents()]);
      } else {
        await refetchAuditEvents();
      }

      return {
        success: result.success,
        message: result.message,
      };
    } catch (mutationError) {
      await refetchAuditEvents();
      return {
        success: false,
        message: mutationError instanceof Error ? mutationError.message : "We were not able to disable this connector.",
      };
    }
  };

  const runTestConnectorConnection = async (
    connector: CatalogConnector,
    authMode: ConnectorAuthMode,
    config: ConnectorCredentialConfig
  ): Promise<ConnectorActionResult> => {
    try {
      const response = await testConnectorConnectionMutation({
        variables: {
          input: {
            connectorType: connector.connectorType,
            authMode,
            config,
          },
        },
        refetchQueries: [
          {
            query: GetAuditEventsDocument,
            variables: auditEventQueryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });
      const result = response.data?.testConnectorConnection;

      if (!result) {
        return {
          success: false,
          message: "We were not able to test this connector.",
        };
      }

      return {
        success: result.success,
        message: result.message,
        authorizationUrl: result.authorizationUrl,
      };
    } catch (mutationError) {
      return {
        success: false,
        message: mutationError instanceof Error ? mutationError.message : "We were not able to test this connector.",
      };
    }
  };

  const runSetConnectorRoleAccess = async (connectorType: ConnectorType, enabledRoles: string[]) => {
    try {
      setSavingRoleAccessConnectorType(connectorType);
      setRoleAccessErrorMessage(null);
      const response = await setConnectorRoleAccessMutation({
        variables: {
          input: {
            connectorType,
            enabledRoles,
          },
        },
      });
      const result = response.data?.setConnectorRoleAccess;

      if (!result?.success) {
        setRoleAccessErrorMessage(result?.message ?? "We were not able to update connector role access.");
        return;
      }

      await Promise.all([refetchEnabledConnectors(), refetchAuditEvents()]);
    } catch (mutationError) {
      setRoleAccessErrorMessage(
        mutationError instanceof Error ? mutationError.message : "We were not able to update connector role access."
      );
    } finally {
      setSavingRoleAccessConnectorType(null);
    }
  };

  useConnectorStatusToasts({
    enabledConnectors,
    connectorCatalog,
    onSelectConnector: handleSelectConnector,
  });

  return (
    <AdminLayout withPadding={false}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          <View style={styles.pageHeader}>
            <CustomText
              text="Integrations"
              size="xxl"
              useTranslationText={false}
              textType="text"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={styles.pageTitle}
            />
            <CustomText
              text="Connect external services to make content available across Axio"
              size="s"
              useTranslationText={false}
              textType="text"
              weight="normal"
              role="text"
              style={styles.pageSubtitle}
            />
          </View>

          {showInstitutionalIssueBanner ? (
            <View
              accessible={true}
              accessibilityRole="alert"
              accessibilityLabel={institutionalIssueBannerText}
              style={styles.healthBanner}>
              <View style={styles.healthBannerContent}>
                <AlertTriangle size={18} color="#B54708" />
                <CustomText
                  text={institutionalIssueBannerText}
                  size="s"
                  useTranslationText={false}
                  textType="text"
                  weight="medium"
                  role="text"
                  style={styles.healthBannerText}
                />
              </View>
              <View style={styles.healthBannerActions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Review unhealthy institutional connections"
                  onPress={() => setActiveTab("active")}
                  style={({ pressed }) => [styles.healthBannerReviewButton, pressed && styles.healthBannerPressed]}>
                  <CustomText
                    text="Review"
                    size="s"
                    useTranslationText={false}
                    textType="text"
                    weight="bold"
                    role="text"
                    style={styles.healthBannerReviewText}
                  />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss institutional connection alert"
                  onPress={() => setDismissedInstitutionalIssueSignature(institutionalIssueSignature)}
                  hitSlop={8}
                  style={styles.healthBannerDismissButton}>
                  <X size={16} color="#B54708" />
                </Pressable>
              </View>
            </View>
          ) : null}

          <AdminTabNavigator tabData={TABS} activeTab={activeTab} onTabPress={setActiveTab} />

          <View style={styles.tabContent}>
            {activeTab === "catalog" && (
              <CatalogTab
                catalog={connectorCatalog}
                loading={loading}
                errorMessage={catalogErrorMessage}
                connectionMap={connectionMap}
                approvalMap={approvalMap}
                onSelect={handleSelectConnector}
              />
            )}
            {activeTab === "active" && (
              <ActiveConnectionTab
                catalog={connectorCatalog}
                connectionMap={connectionMap}
                approvalMap={approvalMap}
                userConnectionAggregates={userConnectionAggregates}
                onSelect={handleSelectConnector}
                onAddConnection={() => setActiveTab("catalog")}
                onRefresh={async () => {
                  await Promise.all([refetchEnabledConnectors(), refetchConnectorHealth(), refetchAuditEvents()]);
                }}
                onDisconnect={(connector) => runDisableConnector(connector, false)}
              />
            )}
            {activeTab === "rbac" && (
              <AccessControlTab
                catalog={connectorCatalog}
                enabledConnectors={enabledConnectors}
                savingConnectorType={savingRoleAccessConnectorType}
                errorMessage={roleAccessErrorMessage}
                onRoleAccessChange={runSetConnectorRoleAccess}
              />
            )}
            {activeTab === "audit" && (
              <AuditLogTab
                events={auditEvents}
                search={auditEventSearchQuery}
                connectorFilter={auditEventConnectorFilter}
                onSearchChange={setAuditEventSearchQuery}
                onConnectorFilterChange={setAuditEventConnectorFilter}
              />
            )}
          </View>
          <ConnectorDetails
            connector={selectedConnector}
            connection={selectedConnection}
            approval={selectedApproval}
            open={detailPanelOpen}
            onOpenChange={setDetailPanelOpen}
            onApproveApp={(connector) => runEnableConnector(connector, ConnectorAuthMode.UserOauth2, {})}
            onActivate={(connector, authMode, config) => runEnableConnector(connector, authMode, config)}
            onTestConnection={(connector, authMode, config) => runTestConnectorConnection(connector, authMode, config)}
            onDisconnect={(connector) => runDisableConnector(connector, false)}
            onRevokeApproval={(connector) => runDisableConnector(connector, true)}
          />
        </View>
      </SafeAreaView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    paddingBottom: 24,
  },
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  pageHeader: {
    gap: 4,
    marginBottom: 32,
  },
  pageTitle: {
    color: "#101828",
  },
  pageSubtitle: {
    color: "#344054",
  },
  healthBanner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#FEDF89",
    backgroundColor: "#FFFAEB",
    marginBottom: 16,
  },
  healthBannerContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  healthBannerText: {
    color: "#B54708",
  },
  healthBannerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  healthBannerReviewButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#FEDF89",
  },
  healthBannerReviewText: {
    color: "#B54708",
  },
  healthBannerPressed: {
    opacity: 0.75,
  },
  healthBannerDismissButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  tabContent: {},
});
